<template>
  <div class="about-laiqiu">
    <!-- 背景装饰层 -->
    <div class="bg-layer">
      <div class="gradient-orb orb-1"></div>
      <div class="gradient-orb orb-2"></div>
      <div class="grid-overlay"></div>
    </div>

    <!-- 主内容区 -->
    <main class="content-wrapper">
      <!-- Hero Section -->
      <section class="hero-section" ref="heroRef">
        <div class="hero-badge">
          <span class="badge-dot"></span>
          <span>ABOUT</span>
        </div>
        <h1 class="hero-title">
          <span class="title-line">关于来秋</span>
          <span class="title-accent"></span>
        </h1>
        <p class="hero-subtitle">
          在算法洪流中，重建有温度、有灵魂、有理想的数字社区
        </p>
        <div class="hero-divider"></div>
      </section>

      <!-- Introduction Section -->
      <section class="intro-section" ref="introRef">
        <div class="section-label">
          <span class="label-number">01</span>
          <span class="label-text">我们是谁</span>
        </div>
        <div class="intro-content">
          <h2 class="section-heading">
            来秋 —— 一个以<span class="text-gradient">技术、美学、人文</span>为内核的数字社区
          </h2>
          <p class="section-description">
            我们相信，科技最终要服务于人，而非取代人。
            在这个信息爆炸的时代，我们选择慢下来，
            用心创造有价值、有深度、有美感的内容与体验。
          </p>
        </div>
        <div class="glass-card">
          <div class="card-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
          </div>
          <p class="card-quote">"让技术回归人文，让代码拥有温度，让创意自由生长。"</p>
        </div>
      </section>

      <!-- Philosophy Section -->
      <section class="philosophy-section" ref="philosophyRef">
        <div class="section-label">
          <span class="label-number">02</span>
          <span class="label-text">品牌理念</span>
        </div>
        <div class="philosophy-grid">
          <div class="philosophy-item" v-for="(item, index) in philosophyItems" :key="index">
            <div class="item-marker"></div>
            <p class="item-text">{{ item }}</p>
          </div>
        </div>
      </section>

      <!-- Mission Section -->
      <section class="mission-section" ref="missionRef">
        <div class="section-label">
          <span class="label-number">03</span>
          <span class="label-text">核心价值观</span>
        </div>
        <h2 class="mission-headline">
          审美即正义，
          <br />
          <span class="text-muted">技术即表达，</span>
          <br />
          <span class="text-gradient">人文即底线。</span>
        </h2>
        <div class="values-grid">
          <div class="value-card" v-for="(value, index) in values" :key="index"
            :style="{ '--delay': `${index * 100}ms` }">
            <span class="value-emoji">{{ value.icon }}</span>
            <p class="value-text">{{ value.text }}</p>
          </div>
        </div>
      </section>

      <!-- Services Section -->
      <section class="services-section" ref="servicesRef">
        <div class="section-label">
          <span class="label-number">04</span>
          <span class="label-text">我们做什么</span>
        </div>
        <div class="services-list">
          <div class="service-item" v-for="(service, index) in services" :key="index">
            <span class="service-index">{{ String(index + 1).padStart(2, '0') }}</span>
            <span class="service-text">{{ service }}</span>
            <span class="service-arrow">→</span>
          </div>
        </div>
      </section>

      <!-- 团队成员 -->
      <section class="team-section" ref="teamRef">
        <div class="section-label">
          <span class="label-number">05</span>
          <span class="label-text">核心团队</span>
        </div>
        <h2 class="team-title">我们的成员</h2>
        <div class="team-grid">
          <div class="team-card" v-for="(m, idx) in talentList" :key="idx" :style="{ '--delay': idx * 100 + 'ms' }">
            <div class="avatar-box">
              <img :src="m.avatar" alt="" class="avatar-img" />
            </div>
            <h3 class="member-name">{{ m.name }}</h3>
            <p class="member-en">{{ m.enName }}</p>
            <p class="member-job">{{ m.job }}</p>
            <div class="member-desc">
              <p v-for="(line, i) in m.desc" :key="i">{{ line }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Join Section -->
      <section class="join-section" ref="joinRef">
        <div class="join-content">
          <h2 class="join-title">加入我们</h2>
          <p class="join-description">
            无论你是开发者、设计师、创作者、思考者，
            只要你认同我们的理念，你就是来秋的一部分。
          </p>
          <div class="join-highlight">
            <span class="highlight-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <circle cx="12" cy="12" r="10" />
                <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                <line x1="9" y1="9" x2="9.01" y2="9" />
                <line x1="15" y1="9" x2="15.01" y2="9" />
              </svg>
            </span>
            <p>这里没有喧嚣，只有志同道合的同行者。</p>
          </div>
        </div>
      </section>

      <!-- Footer -->
      <footer class="footer">
        <div class="footer-divider"></div>
        <div class="footer-content">
          <p class="copyright">© 2026 LAIQIU COMMUNITY</p>
          <p class="tagline">科技以人文的方式着陆</p>
        </div>
      </footer>
    </main>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const heroRef = ref(null)
const introRef = ref(null)
const philosophyRef = ref(null)
const missionRef = ref(null)
const servicesRef = ref(null)
const teamRef = ref(null)
const joinRef = ref(null)

const philosophyItems = ref([
  '来秋诞生于数字时代的喧嚣之中。',
  '我们希望在算法统治的世界里，保留一份属于人类的温度、思考与审美。',
  '我们不追求流量，不制造焦虑，只专注于创造有价值、有深度、有美感的内容与体验。'
])

const values = ref([
  { icon: '☆', text: '保持敬畏，保持好奇' },
  { icon: '◇', text: '尊重原创，尊重思想' },
  { icon: '△', text: '技术向善，美学至上' },
  { icon: '○', text: '拒绝浮躁，拒绝同质化' }
])

const services = ref([
  '高质量科技与美学内容分享',
  '开源技术与数字艺术创作',
  '深度思考与跨学科探索',
  '构建安静、纯粹、高质量的创作者社区'
])

// 优化后的顶级大厂成员介绍
const talentList = ref([
  {
    name: "林子淇",
    enName: "LIN ZIQI",
    job: "FOUNDER / ARCHITECT",
    avatar: require("@/assets/head_url/lqaqiu.png"),
    desc: [
      "来秋社区创始人 & 技术负责人",
      "全栈架构师 | 产品与体验总设计",
      "AI 战略规划与智能体系设计者"
    ]
  },
  {
    name: "王俊超",
    enName: "CHAO WANG",
    job: "FRONTEND DIRECTOR",
    avatar: require("@/assets/head_url/junchao.png"),
    desc: [
      "前端技术总监",
      "用户界面与交互体系负责人",
      "视觉工程与前端架构构建者"
    ]
  },
  {
    name: "张萌",
    enName: "MONA ZHANG",
    job: "CREATIVE DIRECTOR",
    avatar: require("@/assets/head_url/yud.png"),
    desc: [
      "创意设计总监",
      "品牌视觉与用户体验负责人",
      "界面美学与设计语言构建者"
    ]
  },
  {
    name: "徐榕斌",
    enName: "ROBIN XU",
    job: "OPERATIONS LEAD",
    avatar: require("@/assets/head_url/yage.png"),
    desc: [
      "后端技术学习家",
      "多媒体内容与视觉呈现负责人"
    ]
  },
  {
    name: "张靖腾",
    enName: "JT ZHANG",
    job: "COMMUNITY & GROWTH LEAD",
    avatar: "https://free-img.400040.xyz/4/2026/03/22/69bf7e108a594.png",
    desc: [
      "社区生态与增长负责人",
      "用户体系与内容策略构建",
      "品牌传播与社区生态运营"
    ]
  }
])

let observer = null
onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) entry.target.classList.add('is-visible')
    })
  }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' })

  const sections = [heroRef, introRef, philosophyRef, missionRef, servicesRef, teamRef, joinRef]
  sections.forEach(s => s.value && observer.observe(s.value))
})
onUnmounted(() => observer?.disconnect())

</script>

<style scoped>
.about-laiqiu {
  --color-bg: #0a0a0a;
  --color-bg-elevated: #111111;
  --color-bg-card: #141414;
  --color-border: rgba(255, 255, 255, 0.06);
  --color-border-hover: rgba(255, 255, 255, 0.12);
  --color-text-primary: #ffffff;
  --color-text-secondary: #a0a0a0;
  --color-text-muted: #666666;
  --color-accent: #f0f0f0;
  --color-accent-warm: #e8d5b7;
  --color-glow: rgba(255, 255, 255, 0.08);
  --space-xs: 0.5rem;
  --space-sm: 1rem;
  --space-md: 2rem;
  --space-lg: 4rem;
  --space-xl: 8rem;
  --space-2xl: 12rem;
  --font-sans: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  --font-serif: 'Playfair Display', Georgia, serif;
  --transition-fast: 150ms ease;
  --transition-base: 300ms ease;
  --transition-slow: 500ms cubic-bezier(0.4, 0, 0.2, 1);
}

.bg-layer {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}

.gradient-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(120px);
  opacity: 0.4;
}

.orb-1 {
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(60, 60, 60, 0.3) 0%, transparent 70%);
  top: -200px;
  right: -100px;
}

.orb-2 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(80, 70, 60, 0.2) 0%, transparent 70%);
  bottom: 20%;
  left: -100px;
}

.grid-overlay {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px);
  background-size: 80px 80px;
}

.content-wrapper {
  position: relative;
  z-index: 1;
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 var(--space-md);
}

[class*="-section"] {
  padding: var(--space-xl) 0;
  opacity: 0;
  transform: translateY(30px);
  transition: opacity var(--transition-slow), transform var(--transition-slow);
}

[class*="-section"].is-visible {
  opacity: 1;
  transform: translateY(0);
}

.section-label {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  margin-bottom: var(--space-lg);
}

.label-number {
  font-family: monospace;
  font-size: 0.75rem;
  color: var(--color-text-muted);
  letter-spacing: 0.1em;
}

.label-text {
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.15em;
}

.section-heading {
  font-family: var(--font-sans);
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  font-weight: 500;
  line-height: 1.3;
  color: var(--color-text-primary);
  margin-bottom: var(--space-md);
  letter-spacing: -0.02em;
}

.section-description {
  font-size: 1.125rem;
  line-height: 1.8;
  color: var(--color-text-secondary);
  max-width: 65ch;
}

.hero-section {
  min-height: 70vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: var(--space-2xl);
  padding-bottom: var(--space-xl);
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  padding: var(--space-xs) var(--space-sm);
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: 100px;
  font-size: 0.75rem;
  color: var(--color-text-secondary);
  letter-spacing: 0.2em;
  margin-bottom: var(--space-md);
  width: fit-content;
}

.badge-dot {
  width: 6px;
  height: 6px;
  background: var(--color-accent-warm);
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {

  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.5;
    transform: scale(1.2);
  }
}

.hero-title {
  font-family: var(--font-sans);
  font-size: clamp(3.5rem, 12vw, 8rem);
  font-weight: 600;
  line-height: 0.95;
  letter-spacing: -0.04em;
  color: var(--color-text-primary);
  margin-bottom: var(--space-md);
}

.title-line {
  display: block;
}

.title-accent {
  display: block;
  background: linear-gradient(135deg, var(--color-text-primary), var(--color-accent-warm));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: clamp(1.125rem, 2.5vw, 1.5rem);
  color: var(--color-text-secondary);
  max-width: 50ch;
  line-height: 1.6;
  margin-bottom: var(--space-lg);
}

.hero-divider {
  width: 60px;
  height: 1px;
  background: linear-gradient(90deg, var(--color-border-hover), transparent);
}

.intro-section {
  border-top: 1px solid var(--color-border);
}

.glass-card {
  margin-top: var(--space-lg);
  padding: var(--space-lg);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.01));
  border: 1px solid var(--color-border);
  border-radius: 16px;
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  gap: var(--space-md);
  transition: .3s;
}

.glass-card:hover {
  border-color: var(--color-border-hover);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02));
}

.card-icon {
  width: 48px;
  height: 48px;
  color: var(--color-accent-warm);
}

.card-icon svg {
  width: 100%;
  height: 100%;
}

.card-quote {
  font: italic 1.25rem var(--font-serif);
  color: var(--color-text-primary);
  line-height: 1.6;
  margin: 0;
}

.philosophy-section {
  border-top: 1px solid var(--color-border);
}

.philosophy-grid {
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
}

.philosophy-item {
  display: flex;
  align-items: flex-start;
  gap: var(--space-sm);
  padding: var(--space-md);
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  transition: .3s;
}

.philosophy-item:hover {
  border-color: var(--color-border-hover);
  transform: translateX(8px);
}

.item-marker {
  width: 8px;
  height: 8px;
  margin-top: 8px;
  background: var(--color-accent-warm);
  border-radius: 2px;
  opacity: .6;
  transition: .3s;
}

.philosophy-item:hover .item-marker {
  opacity: 1;
}

.item-text {
  font-size: 1.0625rem;
  color: var(--color-text-secondary);
  line-height: 1.7;
  margin: 0;
}

.mission-section {
  border-top: 1px solid var(--color-border);
}

.mission-headline {
  font: 500 clamp(2rem, 5vw, 3.5rem)/1.2 var(--font-sans);
  color: var(--color-text-primary);
  letter-spacing: -0.02em;
  margin-bottom: var(--space-xl);
}

.text-muted {
  color: var(--color-text-muted);
}

.text-gradient {
  background: linear-gradient(135deg, var(--color-accent-warm), var(--color-accent));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.values-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--space-md);
}

.value-card {
  padding: var(--space-md);
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  transition: .3s;
  transition-delay: var(--delay);
}

.value-card:hover {
  border-color: var(--color-border-hover);
  transform: translateY(-4px);
  box-shadow: 0 20px 40px #0003;
}

.value-emoji {
  display: block;
  font-size: 1.5rem;
  margin-bottom: var(--space-sm);
  opacity: .8;
}

.value-text {
  font-size: .9375rem;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.5;
}

.services-section {
  border-top: 1px solid var(--color-border);
}

.services-list {
  display: flex;
  flex-direction: column;
}

.service-item {
  display: grid;
  grid-template-columns: 60px 1fr auto;
  align-items: center;
  gap: var(--space-md);
  padding: var(--space-md) 0;
  border-bottom: 1px solid var(--color-border);
  transition: .3s;
}

.service-item:first-child {
  border-top: 1px solid var(--color-border);
}

.service-item:hover {
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.02), transparent);
}

.service-index {
  font: monospace;
  font-size: .875rem;
  color: var(--color-text-muted);
  letter-spacing: .1em;
}

.service-text {
  font-size: 1.125rem;
  color: var(--color-text-primary);
  transition: .3s;
}

.service-item:hover .service-text {
  color: var(--color-accent);
}

.service-arrow {
  color: var(--color-text-muted);
  opacity: 0;
  transition: .3s;
}

.service-item:hover .service-arrow {
  transform: translateX(8px);
  opacity: 1;
}

/* 团队成员 */
.team-section {
  border-top: 1px solid var(--color-border);
}

.team-title {
  font-size: clamp(1.8rem, 4vw, 2.4rem);
  font-weight: 500;
  color: var(--color-text-primary);
  margin-bottom: var(--space-lg);
}

.team-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: var(--space-md);
}

.team-card {
  padding: var(--space-md);
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: 20px;
  text-align: center;
  transition: all 0.4s ease;
  opacity: 0;
  transform: translateY(20px);
}

.team-section.is-visible .team-card {
  opacity: 1;
  transform: translateY(0);
  transition-delay: var(--delay);
}

.team-card:hover {
  border-color: var(--color-accent-warm);
  transform: translateY(-6px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.35);
}

.avatar-box {
  width: 90px;
  height: 90px;
  margin: 0 auto var(--space-md);
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  border: 1px solid var(--color-border-hover);
}

.member-name {
  font-size: 1.3rem;
  color: var(--color-text-primary);
  margin-bottom: 4px;
}

.member-en {
  font-size: 0.75rem;
  color: var(--color-text-muted);
  letter-spacing: 1px;
  margin-bottom: 6px;
}

.member-job {
  font-size: 0.85rem;
  color: var(--color-accent-warm);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
}

.member-desc p {
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  line-height: 1.5;
  margin: 0 0 4px 0;
}

.join-section {
  border-top: 1px solid var(--color-border);
}

.join-content {
  max-width: 600px;
}

.join-title {
  font: 500 clamp(2rem, 4vw, 3rem) var(--font-sans);
  color: var(--color-text-primary);
  letter-spacing: -0.02em;
  margin-bottom: var(--space-md);
}

.join-description {
  font-size: 1.125rem;
  color: var(--color-text-secondary);
  line-height: 1.8;
  margin-bottom: var(--space-lg);
}

.join-highlight {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-md);
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  border-left: 3px solid var(--color-accent-warm);
}

.highlight-icon {
  width: 32px;
  height: 32px;
  color: var(--color-accent-warm);
}

.highlight-icon svg {
  width: 100%;
  height: 100%;
}

.join-highlight p {
  font-size: 1rem;
  color: var(--color-text-primary);
  margin: 0;
}

.footer {
  padding: var(--space-xl) 0 var(--space-lg);
}

.footer-divider {
  width: 100%;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--color-border-hover), transparent);
  margin-bottom: var(--space-lg);
}

.footer-content {
  text-align: center;
}

.copyright {
  font: monospace;
  font-size: .875rem;
  color: var(--color-text-muted);
  letter-spacing: .1em;
  margin-bottom: var(--space-xs);
}

.tagline {
  font: italic 1rem var(--font-serif);
  color: var(--color-text-secondary);
  opacity: .6;
}

@media (max-width:768px) {
  .about-laiqiu {
    --space-xl: 5rem;
    --space-2xl: 8rem;
  }

  .hero-section {
    min-height: 60vh;
    padding-top: var(--space-xl);
  }

  .glass-card {
    flex-direction: column;
    text-align: center;
  }

  .philosophy-item:hover {
    transform: none;
  }

  .values-grid {
    grid-template-columns: 1fr;
  }

  .service-item {
    grid-template-columns: 40px 1fr;
  }

  .service-arrow {
    display: none;
  }

  .join-highlight {
    flex-direction: column;
    text-align: center;
  }
}
</style>